import React, { useState, useEffect } from "react";
import "./CreateTask.css";

const CreateTask = ({ onClose, onTaskCreated }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    assignedTo: "",
    deadline: "",
    priority: "medium",
    category: "General",
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState("");

  // Employee selection statess
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // FIXED: Get token properly
  const getAuthToken = () => {
    return localStorage.getItem("authToken");
  };

  // FIXED: Get base URL
  const getBaseURL = () => {
    return process.env.REACT_APP_BACKEND_BASEURL || "";
  };

  useEffect(() => {
    fetchUsers();
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      const dropdown = event.target.closest(".employee-selector-container");
      if (!dropdown) {
        setShowEmployeeDropdown(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // SIMPLE FLAT LIST - NO GROUPING TO AVOID DUPLICATES
  useEffect(() => {
    if (!users || users.length === 0) return;

    let filtered = users.filter((user) => {
      if (!user || !user.name) return false;

      const searchLower = employeeSearch.toLowerCase();
      return (
        user.name.toLowerCase().includes(searchLower) ||
        (user.employeeId &&
          user.employeeId.toLowerCase().includes(searchLower)) ||
        (user.department &&
          user.department.toLowerCase().includes(searchLower)) ||
        (user.position && user.position.toLowerCase().includes(searchLower)) ||
        user.email.toLowerCase().includes(searchLower) ||
        user.role.toLowerCase().includes(searchLower)
      );
    });

    // NO GROUPING - SIMPLE FLAT LIST
    setFilteredUsers(filtered);
  }, [users, employeeSearch]);

  // FIXED: Enhanced fetchUsers function with proper token and base URL
  const fetchUsers = async () => {
    try {
      const token = getAuthToken();
      const baseURL = getBaseURL();

      if (!token) {
        console.error("No auth token found");
        setErrors({ submit: "Authentication required. Please login again." });
        return;
      }

      // Try admin route first for complete user list
      let response = await fetch(`${baseURL}/api/admin/users/active`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      // If admin route fails (not admin), fallback to task users endpoint
      if (!response.ok || response.status === 403) {
        console.log("Admin route failed, trying fallback...");
        response = await fetch(`${baseURL}/api/tasks/users`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.users) {
        // Sort users by role priority and then by name for better display
        const sortedUsers = data.users.sort((a, b) => {
          const roleOrder = { admin: 4, hr: 3, supervisor: 2, employee: 1 };
          if (roleOrder[b.role] !== roleOrder[a.role]) {
            return (roleOrder[b.role] || 0) - (roleOrder[a.role] || 0);
          }
          return a.name.localeCompare(b.name);
        });
        setUsers(sortedUsers);
        console.log(`Loaded ${sortedUsers.length} users for assignment`);
      } else {
        console.error("Invalid response format:", data);
        setErrors({ submit: "Failed to load users for assignment" });
      }
    } catch (error) {
      console.error("Error fetching users:", error);

      // Try fallback endpoint if first attempt fails
      try {
        const fallbackToken = getAuthToken();
        const fallbackBaseURL = getBaseURL();

        const response = await fetch(`${fallbackBaseURL}/api/tasks/users`, {
          headers: {
            Authorization: `Bearer ${fallbackToken}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.users) {
            setUsers(data.users);
            console.log(
              `Loaded ${data.users.length} users from fallback endpoint`
            );
          }
        } else {
          throw new Error("Fallback also failed");
        }
      } catch (fallbackError) {
        console.error("Error fetching users (fallback):", fallbackError);
        setErrors({
          submit:
            "Unable to load users. Please check your connection and try again.",
        });
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleEmployeeSelect = (user) => {
    setSelectedEmployee(user);
    setFormData((prev) => ({
      ...prev,
      assignedTo: user._id,
    }));
    setShowEmployeeDropdown(false);
    setEmployeeSearch("");

    // Clear assignment error
    if (errors.assignedTo) {
      setErrors((prev) => ({
        ...prev,
        assignedTo: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!formData.assignedTo) {
      newErrors.assignedTo = "Please select someone to assign the task";
    }

    if (!formData.deadline) {
      newErrors.deadline = "Deadline is required";
    } else {
      const selectedDate = new Date(formData.deadline);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        newErrors.deadline = "Deadline cannot be in the past";
      }
    }

    return newErrors;
  };

  // FIXED: Submit function with proper token and base URL
  const handleSubmit = async (e) => {
    e.preventDefault();

    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    try {
      setLoading(true);
      setErrors({});
      setSuccess("");

      const token = getAuthToken();
      const baseURL = getBaseURL();

      if (!token) {
        setErrors({ submit: "Authentication required. Please login again." });
        return;
      }

      console.log("Creating task with data:", formData);

      const response = await fetch(`${baseURL}/api/tasks/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      console.log("Create task response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Create task error response:", errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Create task response data:", data);

      // SUCCESS CHECK
      if (response.status === 201 && data.success) {
        // Show success message
        setSuccess("✅ Task created successfully!");

        // Call parent callback
        if (onTaskCreated && data.task) {
          onTaskCreated(data.task);
        }

        // Reset form
        setFormData({
          title: "",
          description: "",
          assignedTo: "",
          deadline: "",
          priority: "medium",
          category: "General",
        });
        setSelectedEmployee(null);
        setEmployeeSearch("");

        // Close modal after 1.5 seconds
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        // Error case
        console.error("Error creating task:", data.message);
        setErrors({ submit: data.message || "Failed to create task" });
      }
    } catch (error) {
      console.error("Error creating task:", error);
      setErrors({
        submit: "Network error. Please check your connection and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Role colors for better visualization
  const getRoleColor = (role) => {
    const colors = {
      admin: "#dc3545", // Red
      hr: "#28a745", // Green
      supervisor: "#fd7e14", // Orange
      employee: "#007bff", // Blue
    };
    return colors[role] || "#6c757d";
  };

  // Set minimum date to today
  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="modal-overlay">
      <div className="create-task-modal">
        <div className="modal-header">
          <h2>Create New Task</h2>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* SUCCESS MESSAGE */}
        {success && (
          <div className="alert alert-success">
            <i className="fas fa-check-circle"></i> {success}
          </div>
        )}

        {/* ERROR MESSAGE */}
        {errors.submit && (
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-triangle"></i> {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} className="create-task-form">
          <div className="form-group">
            <label htmlFor="title">Task Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter task title..."
              className={errors.title ? "error" : ""}
            />
            {errors.title && <span className="error-text">{errors.title}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter task description..."
              rows="4"
              className={errors.description ? "error" : ""}
            />
            {errors.description && (
              <span className="error-text">{errors.description}</span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="assignedTo">Assign To (Anyone) *</label>
              <div className="employee-selector-container">
                <div
                  className={`employee-selector ${
                    errors.assignedTo ? "error" : ""
                  }`}
                  onClick={() => setShowEmployeeDropdown(!showEmployeeDropdown)}
                >
                  {selectedEmployee ? (
                    <div className="selected-employee">
                      <div
                        className="employee-avatar"
                        style={{
                          backgroundColor: getRoleColor(selectedEmployee.role),
                        }}
                      >
                        {selectedEmployee.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="employee-info">
                        <div className="employee-name">
                          {selectedEmployee.name}
                        </div>
                        <div className="employee-details">
                          <span
                            className="employee-role"
                            style={{
                              backgroundColor:
                                getRoleColor(selectedEmployee.role) + "20",
                              color: getRoleColor(selectedEmployee.role),
                            }}
                          >
                            {selectedEmployee.role.toUpperCase()}
                          </span>
                          {selectedEmployee.employeeId && (
                            <span> • ID: {selectedEmployee.employeeId}</span>
                          )}
                          {selectedEmployee.department && (
                            <span> • {selectedEmployee.department}</span>
                          )}
                          {selectedEmployee.position && (
                            <span> • {selectedEmployee.position}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <span className="placeholder">
                      Select anyone to assign task...
                    </span>
                  )}
                  <i
                    className={`fas fa-chevron-${
                      showEmployeeDropdown ? "up" : "down"
                    }`}
                  ></i>
                </div>

                {showEmployeeDropdown && (
                  <div className="employee-dropdown">
                    <div className="employee-search">
                      <i className="fas fa-search"></i>
                      <input
                        type="text"
                        placeholder="Search by name, role, department, ID..."
                        value={employeeSearch}
                        onChange={(e) => setEmployeeSearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>

                    <div className="employees-list">
                      {filteredUsers.length === 0 ? (
                        <div className="no-results">
                          {users.length === 0
                            ? "Loading users..."
                            : "No users found"}
                        </div>
                      ) : (
                        filteredUsers.map((user) => (
                          <div
                            key={user._id}
                            className="employee-option"
                            onClick={() => handleEmployeeSelect(user)}
                          >
                            <div
                              className="employee-avatar"
                              style={{
                                backgroundColor: getRoleColor(user.role),
                              }}
                            >
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="employee-info">
                              <div className="employee-name">{user.name}</div>
                              <div className="employee-details">
                                <span
                                  className="employee-role"
                                  style={{
                                    backgroundColor:
                                      getRoleColor(user.role) + "20",
                                    color: getRoleColor(user.role),
                                  }}
                                >
                                  {user.role.toUpperCase()}
                                </span>
                                {user.employeeId && (
                                  <span> • ID: {user.employeeId}</span>
                                )}
                                {user.position && (
                                  <span> • {user.position}</span>
                                )}
                                {user.department && (
                                  <span> • {user.department}</span>
                                )}
                              </div>
                              <div className="employee-email">{user.email}</div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              {errors.assignedTo && (
                <span className="error-text">{errors.assignedTo}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="deadline">Deadline *</label>
              <input
                type="date"
                id="deadline"
                name="deadline"
                value={formData.deadline}
                onChange={handleInputChange}
                min={today}
                className={errors.deadline ? "error" : ""}
              />
              {errors.deadline && (
                <span className="error-text">{errors.deadline}</span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="priority">Priority</label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="category">Category</label>
              <input
                type="text"
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                placeholder="Task category..."
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? "Creating..." : "Create Task"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTask;
